import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ArrowUpDown, Search, FileText, Landmark, CreditCard, Car, Shield, Receipt, X } from "lucide-react";

const FONTE_LABELS = {
  fattura: { label: "Fattura Passiva", icon: FileText, color: "bg-blue-100 text-blue-800" },
  fattura_attiva: { label: "Fattura Attiva", icon: Receipt, color: "bg-green-100 text-green-800" },
  ammortamento: { label: "Ammortamento", icon: Landmark, color: "bg-purple-100 text-purple-800" },
  ammortamento_manuale: { label: "Amm. Manuale", icon: Landmark, color: "bg-purple-100 text-purple-800" },
  movimento_banca: { label: "Mov. Bancario", icon: Landmark, color: "bg-cyan-100 text-cyan-800" },
  movimento_carta: { label: "Mov. Carta", icon: CreditCard, color: "bg-orange-100 text-orange-800" },
  assicurazione: { label: "Assicurazione", icon: Shield, color: "bg-yellow-100 text-yellow-800" },
  bollo: { label: "Bollo", icon: Car, color: "bg-yellow-100 text-yellow-800" },
  leasing: { label: "Leasing", icon: FileText, color: "bg-indigo-100 text-indigo-800" },
  costo_extra: { label: "Costo Extra", icon: FileText, color: "bg-gray-100 text-gray-800" },
  costo_personale: { label: "Personale", icon: FileText, color: "bg-pink-100 text-pink-800" },
  rateo: { label: "Rateo", icon: FileText, color: "bg-teal-100 text-teal-800" },
  carburante: { label: "Carburante", icon: Car, color: "bg-amber-100 text-amber-800" },
};

function getFonteInfo(fonte) {
  return FONTE_LABELS[fonte] || { label: fonte || "Altro", icon: FileText, color: "bg-gray-100 text-gray-800" };
}

export default function DrilldownDialog({ open, onClose, entries, contoNome, contoCodice, columnLabel, formatCurrency }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("importo");
  const [sortDir, setSortDir] = useState("desc");

  const filtered = useMemo(() => {
    let items = [...(entries || [])];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(e =>
        (e.descrizione || "").toLowerCase().includes(q) ||
        (e.fonte || "").toLowerCase().includes(q) ||
        (e.centro_costo_nome || "").toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      const va = sortField === "importo" ? (a.importo || 0) : (a[sortField] || "");
      const vb = sortField === "importo" ? (b.importo || 0) : (b[sortField] || "");
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return items;
  }, [entries, searchTerm, sortField, sortDir]);

  const totale = useMemo(() => (entries || []).reduce((s, e) => s + (e.importo || 0), 0), [entries]);

  const toggleSort = (field) => {
    if (sortField === field) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortField(field); setSortDir("desc"); }
  };

  // Raggruppa per fonte per il riepilogo
  const perFonte = useMemo(() => {
    const map = {};
    for (const e of (entries || [])) {
      const f = e.fonte || "altro";
      if (!map[f]) map[f] = { count: 0, totale: 0 };
      map[f].count++;
      map[f].totale += (e.importo || 0);
    }
    return Object.entries(map).sort((a, b) => b[1].totale - a[1].totale);
  }, [entries]);

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="text-base">Dettaglio</span>
            {contoCodice && <Badge variant="outline" className="font-mono text-xs">{contoCodice}</Badge>}
            <span className="text-base font-semibold">{contoNome}</span>
            <Badge className="ml-auto bg-slate-800 text-white">{columnLabel}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Riepilogo per fonte */}
        <div className="flex flex-wrap gap-1.5 pb-2 border-b">
          {perFonte.map(([fonte, info]) => {
            const fi = getFonteInfo(fonte);
            return (
              <Badge key={fonte} variant="outline" className={`text-[10px] gap-1 ${fi.color}`}>
                {fi.label}: {info.count} ({formatCurrency(info.totale)})
              </Badge>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per descrizione, fonte, centro di costo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-9"
            data-testid="drilldown-search"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 font-medium">
                  <button onClick={() => toggleSort("fonte")} className="flex items-center gap-1 hover:text-foreground text-muted-foreground">
                    Fonte <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left p-2 font-medium">
                  <button onClick={() => toggleSort("descrizione")} className="flex items-center gap-1 hover:text-foreground text-muted-foreground">
                    Descrizione <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left p-2 font-medium">CdC</th>
                <th className="text-left p-2 font-medium">Periodo</th>
                <th className="text-right p-2 font-medium">
                  <button onClick={() => toggleSort("importo")} className="flex items-center gap-1 ml-auto hover:text-foreground text-muted-foreground">
                    Importo <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center p-4 text-muted-foreground italic">Nessun elemento trovato</td></tr>
              ) : (
                filtered.map((entry, idx) => {
                  const fi = getFonteInfo(entry.fonte);
                  const Icon = fi.icon;
                  return (
                    <tr key={idx} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} hover:bg-slate-100 transition-colors`} data-testid={`drilldown-row-${idx}`}>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[10px] gap-1 ${fi.color}`}>
                          <Icon className="h-3 w-3" />
                          {fi.label}
                        </Badge>
                      </td>
                      <td className="p-2 max-w-[300px]">
                        <span className="text-sm truncate block" title={entry.descrizione}>{entry.descrizione || "-"}</span>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{entry.centro_costo_nome || "-"}</td>
                      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                        {entry.data_inizio ? new Date(entry.data_inizio).toLocaleDateString('it-IT') : "-"}
                        {entry.data_fine && ` - ${new Date(entry.data_fine).toLocaleDateString('it-IT')}`}
                      </td>
                      <td className="p-2 text-right font-mono font-medium">{formatCurrency(entry.importo)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 sticky bottom-0">
                <tr>
                  <td colSpan={4} className="p-2 font-semibold text-sm">
                    Totale ({filtered.length} {filtered.length === 1 ? "voce" : "voci"})
                  </td>
                  <td className="p-2 text-right font-mono font-bold text-sm">{formatCurrency(totale)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
